import React from 'react';
import styled from 'styled-components';

const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.$isFromMe ? 'flex-end' : 'flex-start')};
  margin-top: 1px;
  margin-bottom: ${(props) => (props.$showTimestamp ? '12px' : '2px')};
`;

const MessageBubble = styled.div`
  max-width: 70%;
  padding: 10px 14px;
  border-radius: 18px;
  border-bottom-${(props) => (props.$isFromMe ? 'right' : 'left')}-radius: 4px;
  background-color: ${(props) =>
    props.$isFromMe
      ? 'var(--clr-primary, #007bff)'
      : 'var(--clr-bg-secondary, #2a2a2a)'};
  border: ${(props) =>
    props.$isFromMe ? 'none' : '1px solid var(--clr-border, #444)'};
  position: relative;
  word-wrap: break-word;
  word-break: break-word;
`;

const ReplyPreview = styled.div`
  background: rgba(255, 255, 255, 0.08);
  border-left: 3px solid ${(props) => props.$replyColor || 'rgba(255, 255, 255, 0.4)'};
  padding: 4px 8px;
  margin-bottom: 6px;
  border-radius: 0 6px 6px 0;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.14);
  }
`;

const ReplyPreviewHeader = styled.div`
  font-size: 11px;
  color: ${(props) => props.$replyColor || 'rgba(255, 255, 255, 0.7)'};
  font-weight: 500;
  margin-bottom: 1px;
`;

const ReplyPreviewText = styled.div`
  font-size: 12px;
  opacity: 0.75;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 240px;
`;

const MessageText = styled.div`
  color: ${(props) => {
    if (props.$isFromMe) {
      return '#ffffff';
    }
    return props.$color || 'var(--clr-text, #ffffff)';
  }};
  font-size: 0.95rem;
  line-height: 1.4;
  font-family: ${(props) => props.$font || 'inherit'};
  font-weight: ${(props) => (props.$bold ? '600' : 'normal')};

  ${(props) =>
    props.$effect === 'rainbow' &&
    `
    background: linear-gradient(45deg, #ff6b6b, #4ecdc4, #45b7d1, #96ceb4, #ffeaa7, #fd79a8);
    background-size: 400% 400%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: rainbow 3s ease-in-out infinite;
    
    @keyframes rainbow {
      0%, 100% {
        background-position: 0% 50%;
      }
      50% {
        background-position: 100% 50%;
      }
    }
  `}

  ${(props) =>
    props.$effect === 'fade' &&
    `
    animation: fadeInOut 2s ease-in-out infinite;
    
    @keyframes fadeInOut {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0.5;
      }
    }
  `}
`;

const MessageMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  font-size: 0.75rem;
  color: var(--clr-text-secondary, #aaa);
  justify-content: ${(props) => (props.$isFromMe ? 'flex-end' : 'flex-start')};
`;

const SenderName = styled.span`
  color: ${(props) => props.$color || 'var(--clr-text-secondary, #aaa)'};
  font-weight: 500;
`;

const Timestamp = styled.span`
  color: var(--clr-text-disabled, #666);
`;

const ReadIndicator = styled.span`
  color: ${(props) =>
    props.$read
      ? 'var(--clr-primary, #007bff)'
      : 'var(--clr-text-disabled, #666)'};
  font-size: 0.7rem;
`;

function DMMessageBubble({
  message,
  isFromMe,
  friendColor,
  friendNickname,
  showTimestamp = true,
  onReplyClick,
}) {
  const formatTime = (timestamp) => {
    const messageDate = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Check if message is from today
    const isToday = messageDate.toDateString() === today.toDateString();
    
    // Check if message is from yesterday
    const isYesterday = messageDate.toDateString() === yesterday.toDateString();

    const timeString = messageDate.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    if (isToday) {
      return timeString;
    } else if (isYesterday) {
      return `Yesterday ${timeString}`;
    } else {
      // For all other messages, show the date
      const dateString = messageDate.toLocaleDateString([], {
        month: 'short',
        day: 'numeric',
      });
      return `${dateString} ${timeString}`;
    }
  };

  const senderName =
    message.sender?.nickname || (isFromMe ? 'You' : friendNickname || 'Anon');

  return (
    <MessageContainer $isFromMe={isFromMe} $showTimestamp={showTimestamp}>
      <MessageBubble $isFromMe={isFromMe}>
        {message.replyToId && (
          <ReplyPreview
            $replyColor={isFromMe ? 'rgba(255, 255, 255, 0.85)' : message.replyToColor}
            onClick={() => onReplyClick && onReplyClick(message.replyToId)}
          >
            <ReplyPreviewHeader
              $replyColor={isFromMe ? 'rgba(255, 255, 255, 0.85)' : message.replyToColor}
            >
              ↳ {message.replyToNickname || 'Anon'}
            </ReplyPreviewHeader>
            <ReplyPreviewText>{message.replyToText}</ReplyPreviewText>
          </ReplyPreview>
        )}
        <MessageText
          $isFromMe={isFromMe}
          $color={isFromMe ? '#ffffff' : friendColor || message.color}
          $font={message.font}
          $effect={message.effect}
          $bold={isFromMe}
        >
          {message.messageText}
        </MessageText>
      </MessageBubble>

      {showTimestamp && (
        <MessageMeta $isFromMe={isFromMe}>
          {!isFromMe && (
            <SenderName $color={friendColor}>{senderName}</SenderName>
          )}
          <Timestamp>{formatTime(message.createdAt)}</Timestamp>
          {isFromMe && <ReadIndicator $read={false}>✓</ReadIndicator>}
        </MessageMeta>
      )}
    </MessageContainer>
  );
}

export default DMMessageBubble;
